import React, { useCallback, useRef } from 'react'
import { Formik } from 'formik'
import {
  Button,
  Divider,
  FormField,
  Pill,
  Select,
  TextArea,
  TextField,
  TextInput,
} from '../../../../../views/components'
import styles from './PortfolioSettingsForm.module.scss'
import { filter, find, map, pipe } from 'ramda'
import { useEffectOnce } from 'react-use'
import { useDispatch, useSelector } from 'react-redux'
import { chatbotEvents, chatbotSelectors, getLeaseSettingsInitialValues } from 'modules/ui/ChatBot'
import { agencyApiEvents, agencyApiSelectors } from '../../../../api/agency'
import { portfolioApiSelectors } from '../../../../api/portfolio'
import { userApiSelectors } from '../../../../api/user'
import { $TSFixMe } from 'types/ts-migrate'
import { TextFieldTypes } from 'views/components/atoms/TextField/text-field.types'
import { uiEvents } from 'modules/ui/state'
import RouterPromptModal from 'components/organisms/Modals/RouterPromptModal/RouterPromptModal'

const transformSegmentToOption = (segment: any) => ({
  value: segment?.id,
  label: segment?.name,
})

const PortfolioSettingsForm = () => {
  const dispatch = useDispatch()
  const notesRef = useRef<$TSFixMe>()

  useEffectOnce(() => {
    notesRef.current?.focus()
    dispatch(agencyApiEvents.getSegments_request())
  })

  const currentPortfolioId = useSelector(chatbotSelectors.getCurrentPortfolioId)
  const defaultSegmentId = useSelector(userApiSelectors.getCurrentAgencyId)
  const initialValues = useSelector(getLeaseSettingsInitialValues)
  const segments = useSelector(agencyApiSelectors.getSegments)
  const isReadOnly = useSelector(userApiSelectors.isReadOnlyRole)
  const isDraftPortfolio = useSelector(state => portfolioApiSelectors.isDraftPortfolio(state)(currentPortfolioId))

  const segmentOptions = pipe(
    filter((segment: any) => segment?.id !== defaultSegmentId),
    map(transformSegmentToOption),
  )(segments)

  const hasAvailableSegments = segmentOptions.length > 0

  const redirect = useCallback(
    path => {
      dispatch(uiEvents.redirect(path))
    },
    [dispatch],
  )

  const handleSubmit = (values: any): void => {
    dispatch(chatbotEvents.settingsValuesSubmitted(values))
  }

  return (
    <div className={styles.root}>
      <Formik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
        {({
          handleChange,
          handleBlur,
          handleSubmit,
          submitForm,
          setFieldValue,
          values,
          dirty,
          isValid,
          isSubmitting,
        }) => {
          const handleSegmentAdded = (option: any) => {
            const segment = find((s: any) => s.id === option?.value, segments)
            if (!segment) return
            setFieldValue('segments', [...values.segments, segment])
          }

          const handleSegmentRemoved = (segmentId: any): void => {
            setFieldValue(
              'segments',
              values.segments.filter((segment: any) => segment?.id !== segmentId),
            )
          }

          return (
            <form className={styles.form} onSubmit={handleSubmit}>
              <RouterPromptModal
                when={dirty}
                isValid={isValid}
                isSubmitting={isSubmitting}
                onSave={submitForm}
                onDiscard={() => true}
              />
              <fieldset disabled={isReadOnly}>
                <div className={styles.body}>
                  <div className="rbn--row">
                    <div className="rbn--col-md-8">
                      <FormField>
                        <TextField
                          inputComponent={
                            <TextArea
                              name="notes"
                              placeholder=""
                              value={values.notes}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              ref={(ref: any) => {
                                notesRef.current = ref
                              }}
                            />
                          }
                          label="Internal lease notes"
                        />
                      </FormField>
                    </div>
                  </div>

                  <div className="rbn--row">
                    <div className="rbn--col-md-3">
                      <FormField>
                        <TextField
                          inputComponent={
                            <TextInput
                              type={TextFieldTypes.text}
                              name="customLeaseIdentifier"
                              value={values.customLeaseIdentifier}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                          }
                          label="Internal lease ID"
                        />
                      </FormField>
                    </div>
                  </div>

                  <Divider />

                  {/* currently only support one segment per portfolio */}
                  {values.segments.length > 0 ? (
                    <>
                      <h3 className={styles['search-segments-title']}>Lease segment</h3>
                      <div className={styles['segment-pills']}>
                        {values.segments.map((segment: any) => (
                          <Pill key={segment?.id} onRemove={() => handleSegmentRemoved(segment?.id)}>
                            {segment?.name}
                          </Pill>
                        ))}
                      </div>
                    </>
                  ) : (
                    <FormField>
                      <Select
                        name="search-portfolio-segments"
                        label="Lease segment"
                        labelHint="Segments group leases into collections like portfolios or teams, and act as a filter in the Leases and Invoices sections. Segments are managed by account owners."
                        description="Optionally assign this lease to a segment for easier filtering."
                        placeholder="Search segments..."
                        options={segmentOptions}
                        isSearchable={true}
                        isDisabled={!hasAvailableSegments}
                        value={null}
                        onChange={handleSegmentAdded}
                      />
                      {!hasAvailableSegments && (
                        <p>No segments have been set up. Account owners can create segments from the Shortcuts menu.</p>
                      )}
                    </FormField>
                  )}
                </div>
                <div className={styles.footer}>
                  {isDraftPortfolio ? (
                    <>
                      <Button onClick={() => redirect?.(`/leases/${currentPortfolioId}/edit/invoices`)}>Back</Button>
                      {/* Internal is an optional step, so advancing must not require any changes. */}
                      <Button type="submit">Next</Button>
                    </>
                  ) : (
                    <Button disabled={!dirty} type="submit">
                      Save
                    </Button>
                  )}
                </div>
              </fieldset>
            </form>
          )
        }}
      </Formik>
    </div>
  )
}

export default PortfolioSettingsForm
